import { motion } from "framer-motion";
import {
  Crown, Droplets, Flower2, Gift, Grid2x2, Heart, MapPin, Sparkles,
  Sandwich, Cookie, Wine, UtensilsCrossed, Hexagon,
} from "lucide-react";
import { resolveImage } from "@/lib/api";

const HEX_CLIP = "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";

const iconFor = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("miel")) return Droplets;
  if (n.includes("gelée") || n.includes("gelee")) return Sparkles;
  if (n.includes("pollen")) return Flower2;
  if (n.includes("reine")) return Crown;
  if (n.includes("essaim")) return Hexagon;
  if (n.includes("tartinable")) return Sandwich;
  if (n.includes("pâtiss") || n.includes("patiss")) return Cookie;
  if (n.includes("boisson")) return Wine;
  if (n.includes("bien")) return Heart;
  if (n.includes("table") || n.includes("art")) return UtensilsCrossed;
  if (n.includes("visite") || n.includes("ruche")) return MapPin;
  if (n.includes("coffret")) return Gift;
  return Hexagon;
};

function HexTile({ icon: Icon, label, count, active, onClick, testid, index, image }) {
  return (
    <motion.button
      type="button"
      data-testid={testid}
      onClick={onClick}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4) }}
      className="group flex w-[84px] shrink-0 flex-col items-center gap-2 sm:w-[96px]"
    >
      <span
        className={`relative flex h-[64px] w-[72px] items-center justify-center transition-transform duration-300 group-hover:-translate-y-1 sm:h-[72px] sm:w-[80px] ${
          active ? "drop-shadow-[0_6px_14px_rgba(184,115,19,0.35)]" : ""
        }`}
        style={{ clipPath: HEX_CLIP }}
      >
        {/* bord hexagonal */}
        <span
          className={`absolute inset-0 transition-colors duration-300 ${
            active ? "bg-amber-deep" : "bg-amber-900/15 group-hover:bg-amber-deep/50"
          }`}
          style={{ clipPath: HEX_CLIP }}
        />
        {/* intérieur */}
        <span
          className={`absolute inset-[2px] flex items-center justify-center overflow-hidden transition-colors duration-300 ${
            image
              ? "bg-cream"
              : active
              ? "bg-amber-deep text-white"
              : "bg-cream text-amber-deep group-hover:bg-white"
          }`}
          style={{ clipPath: HEX_CLIP }}
        >
          {image && (
            <>
              <img
                src={resolveImage(image)}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <span className={`absolute inset-0 transition-colors duration-300 ${active ? "bg-amber-deep/45" : "bg-bark/35 group-hover:bg-bark/25"}`} />
            </>
          )}
          <Icon
            size={24}
            strokeWidth={1.75}
            className={`relative z-10 ${image ? "text-white drop-shadow" : ""}`}
          />
        </span>
      </span>
      <span className="flex flex-col items-center leading-tight">
        <span className={`text-center text-xs transition-colors ${active ? "font-semibold text-bark" : "font-medium text-bark-soft group-hover:text-bark"}`}>
          {label}
        </span>
        <span className="text-[10px] text-stone-mute">{count} {count > 1 ? "produits" : "produit"}</span>
      </span>
    </motion.button>
  );
}

export default function CategoryRail({ categories, counts, active, onSelect, total }) {
  // Catégories réservées aux apiculteurs, regroupées en fin de liste dans cet ordre
  const BEEKEEPER = ["Visite de ruche", "Essaims", "Reines"];
  const ordered = [
    ...categories.filter((c) => !BEEKEEPER.includes(c.name)),
    ...BEEKEEPER.map((n) => categories.find((c) => c.name === n)).filter(Boolean),
  ];
  return (
    <div data-testid="category-rail" className="relative mb-10 overflow-hidden rounded-3xl border border-amber-900/10 bg-sand/40 px-4 py-6 sm:px-6">
      {/* motif alvéoles discret */}
      <svg className="pointer-events-none absolute -right-6 -top-8 h-40 w-40 text-amber-deep/[0.05]" viewBox="0 0 100 100" fill="currentColor" aria-hidden>
        <polygon points="50,2 93,26 93,74 50,98 7,74 7,26" />
      </svg>
      <div className="relative flex flex-wrap justify-center gap-x-3 gap-y-5 sm:gap-x-4">
        <HexTile
          icon={Grid2x2}
          label="Tout le rucher"
          count={total}
          active={active === "tous"}
          onClick={() => onSelect("tous")}
          testid="filter-tous"
          index={0}
        />
        {ordered.map((c, i) => (
          <HexTile
            key={c.category_id}
            icon={iconFor(c.name)}
            label={c.name}
            count={counts[c.category_id] || 0}
            active={active === c.category_id}
            onClick={() => onSelect(c.category_id)}
            testid={`filter-${c.category_id}`}
            index={i + 1}
            image={c.image_url}
          />
        ))}
      </div>
    </div>
  );
}
